import React, { PropsWithChildren } from 'react';

import HintButton, { WIDE_HINT_WIDTH } from 'components/HintButton';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { IExtendedColumn } from 'utils/grid/types';

import { useDataGridContext } from '../../DataGridContext';
import { useFilterContext } from '../../Filters/FilterContext';

import FilterButton from './FilterButton';
import { Container, StyledArrow, StyledIconButton } from './styled';
import useGridColumnSort from './useGridColumnSort';

interface IHeaderCellData<T> {
  center?: boolean;
  className?: string;
  columns: IExtendedColumn<T>[];
  enableColumnStretch: boolean;
  index: number;
  isEmpty?: boolean;
  isStretched: boolean;
  onToggleColumnStretch: () => void;
  orderColumn?: string;
}

type THeaderCellProps<T> = PropsWithChildren<IHeaderCellData<T>>;

const HeaderCell = <T,>({
  center,
  children,
  className,
  columns,
  enableColumnStretch,
  index,
  isEmpty,
  isStretched,
  onToggleColumnStretch,
}: THeaderCellProps<T>): React.ReactElement => {
  const { disabled, filters } = useDataGridContext();

  const {
    config: { ignoreList, mainColumnsList },
  } = useFilterContext();

  const { cellData, cellTitle, isSortable, onClick, sortedColumn, sortIsOn } =
    useGridColumnSort({ columns, index });

  const { dataKey, hintText } = cellData;

  const getArrowIcon = () => {
    if (sortedColumn?.property !== dataKey) {
      return <ICONS_MAP.LongArrowDownBold />;
    }

    if (sortIsOn) {
      return <StyledArrow $up={sortedColumn?.direction === 'ASC'} />;
    }
  };

  const currentFilter = filters?.[dataKey as string];

  const filterIsAvailable =
    !!currentFilter?.listOperations?.length &&
    !ignoreList?.some((ignoredFilter) => ignoredFilter === dataKey);

  const renderContent = () => {
    if (isEmpty) {
      return null;
    }

    return (
      <>
        {children || (cellTitle as string)}
        {hintText && <HintButton maxHintWidth={WIDE_HINT_WIDTH}>{hintText}</HintButton>}
        {filterIsAvailable && (
          <FilterButton columnKey={dataKey as string} showSelected={false} />
        )}
        {enableColumnStretch && (
          <StyledIconButton
            disabled={disabled}
            iconSize={18}
            onClick={onToggleColumnStretch}
            size='sm'
            startIcon={isStretched ? <ICONS_MAP.Shrink /> : <ICONS_MAP.Stretch />}
            testIds={{ container: `${dataKey as string}StretchButton` }}
            variant='icon'
          />
        )}
        {isSortable && (
          <StyledIconButton
            disabled={disabled}
            iconSize={12}
            isActive={sortedColumn?.property === dataKey && sortIsOn}
            onClick={onClick}
            size='sm'
            startIcon={getArrowIcon()}
            testIds={{ container: `${dataKey as string}SortButton` }}
            variant='icon'
          />
        )}
      </>
    );
  };

  const headerCell = (
    <Container
      $center={center}
      $isMainColumn={mainColumnsList?.some((column) => column === dataKey)}
      className={className}
      data-testid={`${dataKey as string}HeaderCell`}
    >
      {renderContent()}
    </Container>
  );

  return headerCell;
};

export default appReactMemo(HeaderCell);
